/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { RootState } from '@ink-ai/portal/reducers';
import {
  updateWorkflow,
  workflowActions,
} from '@ink-ai/portal/reducers/workflow';
import { PaperCard } from '../components/Common';
import { useNavigationListener } from '../components/hooks/useNavigationListener';
import { AgentApi } from '@ink-ai/insight-service-sdk';
import { getApi } from '@ink-ai/portal/common/requestHelper';
import { useAsyncData } from '@ink-ai/portal/common/hooks/useAsyncData';

export const UpdateWorkflow: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const { id } = useParams();
  const navigate = useNavigate();
  const workflow = useSelector((state: RootState) => state.workflow);

  const { data, isLoadingData, dataError } = useAsyncData(async () => {
    if (!id) return null;
    const workflowApi = await getApi(AgentApi);
    const response = await workflowApi.getAgentById(id);
    return response.data;
  }, [id]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [type, setType] = useState('dify');
  const [errors, setErrors] = useState({
    name: false,
    apiEndpoint: false,
    apiKey: false,
  });

  useNavigationListener();

  useEffect(() => {
    if (!id) {
      navigate('/workflows');
    }
  }, [id, navigate]);

  useEffect(() => {
    if (data) {
      setName(data.name || '');
      setDescription(data.description || '');
      setApiEndpoint(data.apiEndpoint || '');
      setType(data.type || 'mdi');
    }
  }, [data]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (e.target.value.trim() !== '') {
      setErrors((prev) => ({ ...prev, name: false }));
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleApiEndpointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiEndpoint(e.target.value);
    if (e.target.value.trim() !== '') {
      setErrors((prev) => ({ ...prev, apiEndpoint: false }));
    }
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(e.target.value);
    if (e.target.value.trim() !== '') {
      setErrors((prev) => ({ ...prev, apiKey: false }));
    }
  };

  const handleTypeChange = (e: any) => {
    setType(e.target.value);
  };

  const handleCancel = () => {
    navigate('/workflows');
  };

  const handleUpdate = () => {
    const isNameValid = name.trim() !== '';
    const isApiEndpointValid = apiEndpoint.trim() !== '';

    setErrors({
      name: !isNameValid,
      apiEndpoint: !isApiEndpointValid,
      apiKey: false,
    });

    if (!isNameValid || !isApiEndpointValid) return;

    dispatch(workflowActions.setLoading(true));
    dispatch(
      updateWorkflow({
        workflowId: id,
        name,
        description,
        apiEndpoint,
        apiKey,
      }),
    );
  };

  if (isLoadingData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (dataError || !data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
        <Alert severity="error">
          {t('workflows.errors.failedToLoadWorkflow')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
          {t('workflows.navigation.home')}
        </Link>
        <Link
          component={RouterLink}
          to="/workflows"
          sx={{ textDecoration: 'none' }}
        >
          {t('workflows.navigation.workflows')}
        </Link>
        <Typography color="textPrimary">
          {t('workflows.update.title')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left' }}>
          {t('workflows.update.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'left' }}
        >
          {t('workflows.update.description')}
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'left', width: '60%' }}>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'right',
              border: '1px solid #2196f3',
              backgroundColor: '#fff',
              paddingRight: '40px',
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {t('workflows.import.standard.title')}
              </Typography>
              <Typography variant="body2">
                {t('workflows.import.standard.description')}
              </Typography>
            </Box>
          </Alert>
          <TextField
            label={t('workflows.import.name.label')}
            variant="outlined"
            value={name}
            onChange={handleNameChange}
            error={errors.name}
            helperText={
              errors.name ? t('workflows.dialog.form.name.required') : ''
            }
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('workflows.import.description.label')}
            variant="outlined"
            value={description}
            onChange={handleDescriptionChange}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="workflow-type-label">
              {t('workflows.import.type.label')}
            </InputLabel>
            <Select
              labelId="workflow-type-label"
              value={type}
              label={t('workflows.import.type.label')}
              onChange={handleTypeChange}
              disabled
            >
              <MenuItem value="mdi">{t(`workflows.type.mdi`)}</MenuItem>
              <MenuItem value="dify">{t(`workflows.type.dify`)}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('workflows.import.apiEndpoint.label')}
            variant="outlined"
            value={apiEndpoint}
            onChange={handleApiEndpointChange}
            error={errors.apiEndpoint}
            helperText={
              errors.apiEndpoint
                ? t('workflows.dialog.form.apiEndpoint.required')
                : ''
            }
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            sx={{ mb: 2 }}
            placeholder={
              type === 'strands'
                ? 'https://api.medical-deep-insights.com/v1?agentsId=your-agent-id'
                : 'https://api.medical-deep-insights.com/v1'
            }
          />
          <TextField
            label={t('workflows.import.apiKey.label')}
            variant="outlined"
            type="text"
            value={apiKey}
            onChange={handleApiKeyChange}
            error={errors.apiKey}
            helperText={
              errors.apiKey ? t('workflows.dialog.form.apiKey.required') : ''
            }
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            sx={{ mb: 2 }}
            placeholder={t('workflows.update.apiKey.leaveEmptyToKeepCurrent')}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-start',
            mt: 2,
          }}
        >
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={!name || !apiEndpoint}
            sx={{ width: '99px' }}
            loading={workflow.loading}
          >
            {t('workflows.dialog.buttons.update')}
          </LoadingButton>
          <Button onClick={handleCancel} sx={{ ml: 1, width: '99px' }}>
            {t('workflows.dialog.buttons.cancel')}
          </Button>
        </Box>
      </PaperCard>
    </Container>
  );
};
